import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { surface, tone } from '../timeclock/tcFormat';
import { addDays, hours, kindLabel, niceDate, specialLabel, teamError, teamPost } from './teamFormat';
import { AttachmentPicker, CheckList } from './TeamParts';

const KINDS = ['LEAVE', 'RECUP', 'REPLACEMENT', 'SPECIAL', 'SICK'];
const SPECIAL_CODES = ['KLEIN_VERLET', 'GEBOORTE', 'ADOPTIE', 'OUDERSCHAP', 'FAMILIAAL', 'ZORG', 'MANTELZORG', 'OPLEIDING', 'ONBETAALD', 'ANDERE'];

const specialInfo = (t, code) => ({
    KLEIN_VERLET: t('team_special_info_klein_verlet', 'Wedding, death, communion, … (length depends on the event) · paid by the employer'),
    GEBOORTE: t('team_special_info_geboorte', 'Birth of a child, 20 days · first 3 days employer, then health insurance'),
    ADOPTIE: t('team_special_info_adoptie', 'Adoption or foster care · benefit from health insurance'),
    OUDERSCHAP: t('team_special_info_ouderschap', 'Thematic leave, full or part time · benefit from RVA'),
    FAMILIAAL: t('team_special_info_familiaal', 'Unforeseen family emergency, max. 10 days a year · unpaid'),
    ZORG: t('team_special_info_zorg', 'Care for a family member, max. 5 days a year · unpaid'),
    MANTELZORG: t('team_special_info_mantelzorg', 'Thematic leave · benefit from RVA'),
    OPLEIDING: t('team_special_info_opleiding', 'Recognised training during working hours · paid; the employer is refunded'),
    ONBETAALD: t('team_special_info_onbetaald', 'By agreement with management · unpaid'),
    ANDERE: t('team_special_info_andere', 'Explain in the remark · as agreed'),
}[code] || '');

/**
 * New request: kind, period, remark. While filling in, the planner shows
 * what it costs by your roster and which guidelines are met; red ones can
 * still be sent, management then decides (an exception needs a reason).
 * Sickness goes in at once, without approval.
 */
const TeamRequestTab = ({ t, lang, theme, token, me, notify, onChanged }) => {
    const tomorrow = addDays(me.today, 1);
    const [kind, setKind] = useState('LEAVE');
    const [from, setFrom] = useState(tomorrow);
    const [to, setTo] = useState(tomorrow);
    const [special, setSpecial] = useState('KLEIN_VERLET');
    const [remark, setRemark] = useState('');
    const [plan, setPlan] = useState(null);
    const [planError, setPlanError] = useState('');
    const [checking, setChecking] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState('');
    const [attachment, setAttachment] = useState(null);
    const seq = useRef(0);

    const pickKind = (k) => {
        if (!k) return;
        setKind(k);
        setSent('');
        setPlan(null); // the old calculation belongs to the previous kind
        if (k === 'SICK') { setFrom(me.today); setTo(me.today); }
    };

    // Live check, a moment after the last change; only the newest answer counts.
    useEffect(() => {
        if (!from || !to) return undefined;
        const n = ++seq.current;
        setChecking(true);
        const timer = setTimeout(() => {
            teamPost('checkRequest', token, { kind, from, to: to < from ? from : to, special_code: kind === 'SPECIAL' ? special : null }).then((response) => {
                if (n !== seq.current) return;
                setChecking(false);
                if (response?.success) {
                    setPlan(response.data);
                    setPlanError('');
                } else {
                    setPlan(null);
                    setPlanError(teamError(t, response));
                }
            });
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, from, to, special, token]);

    const send = () => {
        if (kind === 'SPECIAL' && !remark.trim()) {
            setSent(t('team_err_remark', 'A remark is required for special leave.'));
            return;
        }
        if (kind === 'SICK' && !attachment) {
            setSent(t('team_err_attachment_required', 'Add the doctor\'s certificate: an attachment is required when you report sick.'));
            return;
        }
        setSending(true);
        setSent('');
        teamPost('createRequest', token, { kind, from, to: to < from ? from : to, special_code: kind === 'SPECIAL' ? special : null, remark,
            attachment: kind === 'SICK' || kind === 'SPECIAL' ? attachment : null }).then((response) => {
            setSending(false);
            if (!response?.success) {
                setSent(teamError(t, response));
                return;
            }
            const direct = response.data.status === 'APPROVED';
            const text = kind === 'SICK'
                ? t('team_sent_sick', 'Reported; the certificate was sent along. Also tell Jeroen yourself.')
                : direct ? t('team_sent_direct', 'Entered and approved (management).')
                    : t('team_sent', 'Sent. Management gets a mail.');
            setSent(text);
            notify(text, true);
            setRemark('');
            setAttachment(null);
            if (onChanged) onChanged();
        });
    };

    const s = surface(theme);
    const total = plan ? plan.total : 0;
    const balance = plan?.balance;
    const field = { '& .MuiInputBase-root': { height: 40 } };

    const after = () => {
        if (kind === 'SICK') return [t('team_cost_sick', 'Sickness'), t('team_cost_sick_d', 'not deducted, no recup')];
        if (kind === 'SPECIAL') return [t('team_kind_special', 'Special leave'), t('team_cost_special_d', 'not deducted anywhere')];
        if (!balance) return null;
        if (kind === 'REPLACEMENT') return [t('team_cost_repl_after', 'Replacement days left after this'), String(balance.after)];
        return [kind === 'RECUP' ? t('team_cost_recup_after', 'Recup after this request') : t('team_cost_leave_after', 'Leave left after this request'), hours(balance.after, lang)];
    };
    const afterLine = after();

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 440px) 1fr' }, gap: 3, alignItems: 'start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: s.faint, mb: 0.75 }}>{t('team_kind', 'Kind')}</Typography>
                    <ToggleButtonGroup exclusive size="small" value={kind} onChange={(e, v) => pickKind(v)} sx={{ flexWrap: 'wrap' }}>
                        {KINDS.map((k) => (
                            <ToggleButton key={k} value={k} sx={{ textTransform: 'none', px: 1.5 }}>
                                {k === 'SICK' ? t('team_report_sick', 'Report sick') : kindLabel(t, k)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField type="date" size="small" label={t('team_from', 'From')} value={from} sx={{ flex: 1, ...field }}
                        onChange={(e) => { setFrom(e.target.value); if (!to || to < e.target.value) setTo(e.target.value); setSent(''); }}
                        slotProps={{ inputLabel: { shrink: true } }} />
                    <TextField type="date" size="small" label={t('team_to', 'Up to and including')} value={to} sx={{ flex: 1, ...field }}
                        onChange={(e) => { setTo(e.target.value); setSent(''); }} slotProps={{ inputLabel: { shrink: true } }} />
                </Box>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: -1 }}>
                    {t('team_whole_days', 'Whole days. A half day, a number of hours or a moved home day is entered by management.')}
                </Typography>
                {kind === 'SPECIAL' && (
                    <Box>
                        <TextField select fullWidth size="small" label={t('team_special_kind', 'Kind of special leave')} value={special} onChange={(e) => setSpecial(e.target.value)} sx={field}>
                            {SPECIAL_CODES.map((c) => <MenuItem key={c} value={c}>{specialLabel(t, c)}</MenuItem>)}
                        </TextField>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.75 }}>{specialInfo(t, special)}</Typography>
                    </Box>
                )}
                <TextField size="small" fullWidth value={remark} onChange={(e) => setRemark(e.target.value)} inputProps={{ maxLength: 500 }}
                    label={kind === 'SPECIAL' ? t('team_remark_special', 'Remark (required for special leave)') : t('team_remark_mgmt', 'Remark for management (optional)')}
                    required={kind === 'SPECIAL'} />
                {kind === 'SICK' && (
                    <Box sx={{ border: '1px solid ' + tone.warn, borderLeftWidth: 4, borderRadius: '6px', px: 1.5, py: 1.25 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: tone.warn, mb: 0.25 }}>
                            {t('team_sick_notify_title', 'Also let management know yourself')}
                        </Typography>
                        <Typography sx={{ fontSize: 13 }}>
                            {t('team_sick_notify_mgmt', 'This report does not replace that: always tell management as soon as possible, preferably Jeroen (e.g. by phone).')}
                        </Typography>
                    </Box>
                )}
                {(kind === 'SICK' || kind === 'SPECIAL') && (
                    <AttachmentPicker t={t} value={attachment} onChange={setAttachment} onError={(text) => setSent(text)}
                        note={kind === 'SICK'
                            ? t('team_attach_note_sick_required', 'Required: the doctor\'s certificate (PDF or photo). Mailed to the sickness mailbox; not stored in the planner.')
                            : t('team_attach_note_special', 'Optional, e.g. an invitation or certificate. Mailed to management; not stored in the planner.')} />
                )}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button variant="contained" disableElevation onClick={send} disabled={sending || !plan || !plan.days.length || (kind === 'SICK' && !attachment)}
                        sx={{ textTransform: 'none', height: 40, px: 2.5 }}>
                        {sending ? t('tc_busy', 'One moment…') : kind === 'SICK' ? t('team_send_sick', 'Report sickness') : me.rights.manager ? t('team_send_direct', 'Enter') : t('team_send', 'Send request')}
                    </Button>
                    {sent && <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{sent}</Typography>}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ ...s.card, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t('team_by_roster', 'By your roster')}</Typography>
                        {checking && <CircularProgress size={14} />}
                    </Box>
                    {planError && <Typography sx={{ fontSize: 13.5, color: tone.bad }}>{planError}</Typography>}
                    {plan && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.4, columnGap: 2, fontSize: 13.5 }}>
                            {plan.days.slice(0, 12).map((d) => (
                                <React.Fragment key={d.day}>
                                    <Box>{niceDate(d.day, lang)}</Box>
                                    <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{hours(d.hours, lang)}</Box>
                                </React.Fragment>
                            ))}
                            {plan.days.length > 12 && <Box sx={{ color: 'text.secondary', gridColumn: '1 / -1' }}>+ {plan.days.length - 12} {t('team_days', 'days')}</Box>}
                            <Box sx={{ fontWeight: 700, borderTop: s.line, pt: 0.5 }}>{t('team_total', 'Total')} · {plan.days.length} {plan.days.length === 1 ? t('team_day', 'day') : t('team_days', 'days')}</Box>
                            <Box sx={{ fontWeight: 700, borderTop: s.line, pt: 0.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{hours(total, lang)}</Box>
                            {afterLine && kind !== 'RECUP' && (
                                <>
                                    <Box sx={{ color: 'text.secondary' }}>{afterLine[0]}</Box>
                                    <Box sx={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                        color: balance && balance.after < 0 ? tone.bad : balance ? tone.ok : 'text.secondary' }}>{afterLine[1]}</Box>
                                </>
                            )}
                            {kind === 'RECUP' && balance && balance.on && (
                                <>
                                    {/* On the day it may dip below zero in November/December; on 31/12 never. */}
                                    <Box sx={{ color: 'text.secondary' }}>{t('team_cost_recup_on', { defaultValue: 'Recup on {{day}} after this request', day: niceDate(balance.on, lang) })}</Box>
                                    <Box sx={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                        color: balance.after >= 0 ? tone.ok : parseInt(balance.on.substring(5, 7), 10) >= 11 && balance.year_end >= 0 ? tone.warn : tone.bad }}>
                                        {hours(balance.after, lang)}
                                    </Box>
                                    <Box sx={{ color: 'text.secondary' }}>{t('team_cost_recup_end', 'Recup on 31 December')}</Box>
                                    <Box sx={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: balance.year_end < 0 ? tone.bad : tone.ok }}>
                                        {hours(balance.year_end, lang)}
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>{t('team_guidelines', 'Guidelines')}</Typography>
                    {kind === 'SICK'
                        ? <CheckList t={t} lang={lang} checks={[]} />
                        : plan && <CheckList t={t} lang={lang} checks={plan.checks} />}
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.25 }}>
                        {kind === 'SICK'
                            ? t('team_sick_note', 'No approval needed: in the calendar at once, and the report with the certificate goes to the sickness mailbox. Colleagues see "away".')
                            : me.rights.manager
                                ? t('team_manager_note', 'As management your own leave goes in at once. Kristof: fill in your leave, so the development rule can be checked.')
                                : t('team_request_note', 'Red is allowed to send; management then decides and needs a reason for an exception. Management gets a mail.')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default TeamRequestTab;
